import { useState, useEffect } from "react";
import axios from "axios";
import { Lock, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Top-right control on the Financials page.
 *
 *   - When no PIN is set: shows "Set PIN for privacy" → click to open a
 *     small popover that captures PIN + Confirm + login password.
 *   - When a PIN is set: shows a real toggle (Switch). Flipping it
 *     OFF asks for the login password and disables the gate WITHOUT
 *     deleting the stored hash. Flipping it back ON does the same.
 *
 * The user explicitly asked that "if the pin is 1234, when they turn
 * it off and then decide to turn it back on it should be the same
 * pass, until changed" — that's exactly what the new
 * /financials/pin/toggle endpoint guarantees.
 */
export default function FinancialsPrivacyToggle({ businessSlug, isDark }) {
  const [status, setStatus] = useState({ loading: true, hasPin: false, enabled: false });
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Toggle-confirmation dialog (replaces the native window.prompt which
  // showed the password in cleartext — security bug). When the owner
  // flips the switch, we open this dialog and capture their login
  // password in a real masked <Input type="password"> field.
  const [toggleDialog, setToggleDialog] = useState({ open: false, nextEnabled: false });
  const [togglePassword, setTogglePassword] = useState("");

  const refresh = async () => {
    try {
      const r = await axios.get(`${BASE_API}/b/${businessSlug}/financials/pin/status`);
      setStatus({ loading: false, hasPin: !!r.data.has_pin, enabled: !!r.data.enabled });
    } catch {
      setStatus({ loading: false, hasPin: false, enabled: false });
    }
  };

  useEffect(() => {
    if (businessSlug) refresh();
  }, [businessSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetPin = async () => {
    if (!/^\d{4,8}$/.test(pin)) {
      toast.error("PIN must be 4–8 digits.");
      return;
    }
    if (pin !== confirmPin) {
      toast.error("PINs don't match.");
      return;
    }
    if (!password) {
      toast.error("Enter your login password to confirm.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BASE_API}/b/${businessSlug}/financials/pin/set`, {
        pin,
        user_password: password,
      });
      toast.success("PIN set. Financials are now locked.");
      setPin("");
      setConfirmPin("");
      setPassword("");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save PIN.");
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (checked) => {
    // No PIN yet → open the setup popover instead of toggling.
    if (!status.hasPin) {
      setOpen(true);
      return;
    }
    // Open the masked-password confirmation dialog. The password input
    // there is a real <Input type="password"> so the value is hidden
    // as the owner types (window.prompt() exposed it before).
    setTogglePassword("");
    setToggleDialog({ open: true, nextEnabled: !!checked });
  };

  // Called when the owner submits the toggle-confirm dialog.
  const confirmToggle = async () => {
    if (!togglePassword) {
      toast.error("Enter your login password to confirm.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BASE_API}/b/${businessSlug}/financials/pin/toggle`, {
        enabled: toggleDialog.nextEnabled,
        user_password: togglePassword,
      });
      toast.success(toggleDialog.nextEnabled ? "Privacy ON. Lock applied." : "Privacy OFF. PIN kept for later.");
      setTogglePassword("");
      setToggleDialog({ open: false, nextEnabled: false });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update protection.");
    } finally {
      setBusy(false);
    }
  };

  if (status.loading) {
    return (
      <div className="inline-flex items-center gap-2 text-xs text-slate-400" data-testid="financials-privacy-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Privacy…
      </div>
    );
  }

  // ---- No PIN yet: clickable "Set PIN" card with popover ----
  if (!status.hasPin) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`h-9 gap-2 ${isDark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-200 hover:bg-slate-50"}`}
            data-testid="financials-set-pin-btn"
          >
            <Lock className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-medium">Set PIN for privacy</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className={`w-80 ${isDark ? "bg-slate-800 border-slate-700 text-white" : "bg-white border-slate-200"}`}
          data-testid="financials-set-pin-popover"
        >
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold">Protect your Financials</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-1">
                A 4–8 digit PIN that gates this page. You can toggle protection on/off later — the PIN is kept either way.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider">New PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={8}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  className="font-mono h-8 text-sm"
                  data-testid="financials-set-pin-new"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider">Confirm</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={8}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  className="font-mono h-8 text-sm"
                  data-testid="financials-set-pin-confirm"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider">Your login password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-8 text-sm"
                autoComplete="current-password"
                data-testid="financials-set-pin-password"
              />
            </div>
            <Button
              type="button"
              size="sm"
              onClick={handleSetPin}
              disabled={busy || pin.length < 4 || !password}
              className="w-full h-8 bg-amber-500 hover:bg-amber-600 text-white text-xs"
              data-testid="financials-set-pin-save"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
              Lock my Financials
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // ---- PIN exists: switch with status text ----
  return (
    <>
      <div
        className={`inline-flex items-center gap-2.5 h-9 px-3 rounded-lg border ${isDark ? "bg-slate-800/80 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}
        data-testid="financials-privacy-toggle-row"
      >
        {status.enabled
          ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
          : <ShieldOff className="w-3.5 h-3.5 text-slate-400" />}
        <span className={`text-xs font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
          Privacy
        </span>
        <Switch
          checked={status.enabled}
          onCheckedChange={handleToggle}
          disabled={busy}
          data-testid="financials-privacy-switch"
        />
        <span
          className={`text-[10px] uppercase tracking-wider font-semibold ${status.enabled ? "text-emerald-500" : "text-slate-400"}`}
          data-testid="financials-privacy-state"
        >
          {status.enabled ? "On" : "Off"}
        </span>
      </div>

      {/* Toggle-confirm dialog — captures the login password in a MASKED
          input. Replaces the previous window.prompt(), which displayed
          the typed password in cleartext in the browser's native dialog. */}
      <Dialog
        open={toggleDialog.open}
        onOpenChange={(o) => { if (!o) { setTogglePassword(""); setToggleDialog({ open: false, nextEnabled: false }); } }}
      >
        <DialogContent
          className={isDark ? "bg-slate-800 border-slate-700 text-white" : ""}
          data-testid="financials-privacy-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {toggleDialog.nextEnabled ? "Turn privacy back on?" : "Turn privacy off?"}
            </DialogTitle>
            <DialogDescription className={isDark ? "text-slate-400" : "text-slate-500"}>
              {toggleDialog.nextEnabled
                ? "Re-enable the Financials PIN gate. Your saved PIN is reused — nothing to retype."
                : "Anyone with access to this dashboard will see Financials without a PIN. Your saved PIN is kept, ready to switch back on later."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label className="text-xs uppercase tracking-wider">Your login password</Label>
            <Input
              type="password"
              value={togglePassword}
              onChange={(e) => setTogglePassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && togglePassword) confirmToggle(); }}
              data-testid="financials-privacy-toggle-password"
              placeholder="••••••••"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setTogglePassword(""); setToggleDialog({ open: false, nextEnabled: false }); }}
              data-testid="financials-privacy-toggle-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmToggle}
              disabled={busy || !togglePassword}
              className={toggleDialog.nextEnabled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}
              data-testid="financials-privacy-toggle-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {toggleDialog.nextEnabled ? "Turn ON" : "Turn OFF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
